// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import { push } from "connected-react-router";
import type { Action } from "redux-action";

import http from "@http";
import { route } from "@routes/helper";
import { isFirstCareUser } from "@modules/account/selectors";
import actionCreators from "@modules/service/actionCreator";
import { getSchema } from "@utils/schema";
import {
  GetNurseDeclinedReasonsList,
  GetNurseDeclinedReason,
  GetNurseDeclinedReasonSchema,
  AddNurseDeclinedReason,
  StartDraftNurseDeclinedReason,
  UpdateNurseDeclinedReason,
  DisableNurseDeclinedReason,
  DiscardNurseDeclinedReasonDraft,
  SubmitNurseDeclinedReasonForReview,
  CancelNurseDeclinedReasonReview,
  CancelNurseDeclinedReasonReviewAndDiscard,
} from "@modules/service/constants/actions";

import { ApprovalStateEnum } from "@modules/service/constants";
import type { ApprovableContainer } from "@modules/service/types";
import type { NurseDeclinedReason } from "@modules/service/reducer/nurse-declined-reasons/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

// READ

type NurseDeclinedReasonListRequest = $Exact<{
  clientId: string,
}>;

export function* onGetNurseDeclinedReasonsList({
  payload,
}: Action<NurseDeclinedReasonListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<ApprovableContainer<NurseDeclinedReason>>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason`,
    );

    yield put(actionCreators.GetNurseDeclinedReasonsListSuccess(response.data.content));
  } catch (e) {
    yield put(actionCreators.GetNurseDeclinedReasonsListError(e.response));
  }
}

type NurseDeclinedReasonRequest = NurseDeclinedReasonListRequest &
  $Exact<{
    reasonId: string,
  }>;

export function* onGetNurseDeclinedReason({
  payload,
}: Action<NurseDeclinedReasonRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<NurseDeclinedReason>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.reasonId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason/${payload.reasonId}`,
    );

    yield put(actionCreators.GetNurseDeclinedReasonSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetNurseDeclinedReasonError(e.response));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  reasonId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  try {
    yield put(actionCreators.GetNurseDeclinedReasonsList({ clientId: params.clientId }));
    if (params.reasonId) {
      yield put(
        actionCreators.GetNurseDeclinedReason({
          clientId: params.clientId,
          reasonId: params.reasonId,
        }),
      );
    }
  } catch (e) {
    throw e;
  }
}

// CREATE

export function* onGetNurseDeclinedReasonSchema(): Generator<*, void, Object> {
  try {
    const schema = getSchema("NurseDeclineReasonRevisionDTO");

    yield put(actionCreators.GetNurseDeclinedReasonSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetNurseDeclinedReasonSchemaError(e.response));
  }
}

export function* onStartDraftNurseDeclinedReason({
  payload,
}: Action<NurseDeclinedReasonRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<NurseDeclinedReason>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.reasonId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason/${payload.reasonId}`,
    );

    yield all([
      put(actionCreators.StartDraftNurseDeclinedReasonSuccess(response.data)),
      put(actionCreators.SetShowEditNurseDeclinedReasonModal({ show: true, step: 1 })),
    ]);
  } catch (e) {
    yield put(actionCreators.StartDraftNurseDeclinedReasonError(e.response));
  }
}

type NurseDeclinedReasonAddRequest = NurseDeclinedReasonListRequest &
  $Exact<{
    draft: NurseDeclinedReason,
  }>;

export function* onAddNurseDeclinedReason({
  payload,
}: Action<NurseDeclinedReasonAddRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.draft) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason`,
      payload.draft,
    );

    yield call(onRefresh, { clientId: payload.clientId });

    yield all([
      put(sendSuccessMessage("DRAFT_CREATED")),
      put(actionCreators.AddNurseDeclinedReasonSuccess(response.data)),
      put(actionCreators.SetShowAddNurseDeclinedReasonModal(false)),
    ]);
  } catch (e) {
    yield put(actionCreators.AddNurseDeclinedReasonError(e.response));
  }
}

// UPDATE

type NurseDeclinedReasonUpdateRequest = NurseDeclinedReasonRequest &
  $Exact<{
    draft: NurseDeclinedReason,
  }>;

export function* onUpdateNurseDeclinedReason({
  payload,
}: Action<NurseDeclinedReasonUpdateRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reasonId || !payload.draft) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason/${payload.reasonId}`,
      payload.draft,
    );

    yield call(onRefresh, { clientId: payload.clientId, reasonId: payload.reasonId });

    yield all([
      put(actionCreators.UpdateNurseDeclinedReasonSuccess(response.data)),
      put(actionCreators.SetShowEditNurseDeclinedReasonModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_NURSE_DECLINED_REASONS", { clientId: payload.clientId }),
        ),
      ),
    ]);
    yield put(sendSuccessMessage("DRAFT_UPDATED"));
  } catch (e) {
    yield put(actionCreators.UpdateNurseDeclinedReasonError(e.response));
  }
}

// DISABLE

export type NurseDeclinedReasonDisableRequest = NurseDeclinedReasonRequest &
  $Exact<{
    approvable: ApprovableContainer<NurseDeclinedReason>,
  }>;

export function* onDisable({
  payload,
}: Action<NurseDeclinedReasonDisableRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<NurseDeclinedReason>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.reasonId || !payload.approvable) {
      throw Error("Invalid payload provided");
    }

    const draft =
      payload.approvable.approvalState === ApprovalStateEnum.ACTIVE_ONLY
        ? payload.approvable.active
        : payload.approvable.draft;

    if (!draft) {
      throw Error("Invalid draft");
    }

    const response = yield call(
      http.put,
      // TODO check v2
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason/${payload.reasonId}/draft`,
      {
        ...draft,
        activeFrom: draft.activeTo ? new Date().toISOString() : draft.activeFrom,
        activeTo: draft.activeTo ? null : new Date().toISOString(),
      },
    );

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason/${payload.reasonId}/submit-for-review`,
      {
        notes: "NurseDeclinedReason disabled and resubmitted for review",
      },
    );

    yield call(onRefresh, { clientId: payload.clientId, reasonId: payload.reasonId });

    yield all([
      put(actionCreators.DisableNurseDeclinedReasonSuccess(response.data)),
      put(actionCreators.SetShowDisableNurseDeclinedReasonModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_NURSE_DECLINED_REASONS", { clientId: payload.clientId }),
        ),
      ),
    ]);
    yield put(sendSuccessMessage("DRAFT_UPDATED"));
  } catch (e) {
    yield put(actionCreators.DisableNurseDeclinedReasonError(e.response));
  }
}

// STATE TRANSITIONS

export type NurseDeclinedReasonTransitionRequest = $Exact<{
  clientId: string,
  reasonId: string,
}>;

export function* onDiscardDraft({
  payload,
}: Action<NurseDeclinedReasonTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.reasonId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason/${payload.reasonId}/draft`,
    );
    yield call(onRefresh, { clientId: payload.clientId });

    yield all([
      put(actionCreators.DiscardNurseDeclinedReasonDraftSuccess()),
      put(actionCreators.SetShowDiscardNurseDeclinedReasonModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_NURSE_DECLINED_REASONS", { clientId: payload.clientId }),
        ),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.DiscardNurseDeclinedReasonDraftError(e.response));
  }
}

export type NurseDeclinedReasonSubmitForReviewRequest = $Exact<{
  ...NurseDeclinedReasonTransitionRequest,
  notes: string,
}>;

export function* onSubmitForReview({
  payload,
}: Action<NurseDeclinedReasonSubmitForReviewRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.reasonId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason/${payload.reasonId}/submit-for-review`,
      {
        notes: payload.notes,
      },
    );

    yield call(onRefresh, { clientId: payload.clientId, reasonId: payload.reasonId });

    yield all([
      put(actionCreators.SubmitNurseDeclinedReasonForReviewSuccess()),
      put(actionCreators.SetShowSubmitNurseDeclinedReasonModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_NURSE_DECLINED_REASONS", { clientId: payload.clientId }),
        ),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitNurseDeclinedReasonForReviewError(e.response));
  }
}

export function* onCancelReview({
  payload,
}: Action<NurseDeclinedReasonTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.reasonId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason/${payload.reasonId}/cancel-review`,
    );

    yield call(onRefresh, { clientId: payload.clientId, reasonId: payload.reasonId });

    yield all([
      put(actionCreators.CancelNurseDeclinedReasonReviewSuccess()),
      put(actionCreators.SetShowCancelNurseDeclinedReasonModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_NURSE_DECLINED_REASONS", { clientId: payload.clientId }),
        ),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelNurseDeclinedReasonReviewError(e.response));
  }
}

export function* onCancelReviewAndDiscard({
  payload,
}: Action<NurseDeclinedReasonTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.reasonId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason/${payload.reasonId}/cancel-review`,
    );
    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason/${payload.reasonId}/draft`,
    );

    yield call(onRefresh, { clientId: payload.clientId });

    yield all([
      put(actionCreators.CancelNurseDeclinedReasonReviewSuccess()),
      put(actionCreators.SetShowCancelDiscardNurseDeclinedReasonModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_NURSE_DECLINED_REASONS", { clientId: payload.clientId }),
        ),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelNurseDeclinedReasonReviewError(e.response));
  }
}

// ROOT

export function* NurseDeclinedReasonListSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetNurseDeclinedReasonsList, onGetNurseDeclinedReasonsList),
    takeEvery(GetNurseDeclinedReason, onGetNurseDeclinedReason),
    takeEvery(GetNurseDeclinedReasonSchema, onGetNurseDeclinedReasonSchema),
    takeEvery(AddNurseDeclinedReason, onAddNurseDeclinedReason),
    takeEvery(StartDraftNurseDeclinedReason, onStartDraftNurseDeclinedReason),
    takeEvery(UpdateNurseDeclinedReason, onUpdateNurseDeclinedReason),
    takeEvery(DisableNurseDeclinedReason, onDisable),
    takeEvery(DiscardNurseDeclinedReasonDraft, onDiscardDraft),
    takeEvery(SubmitNurseDeclinedReasonForReview, onSubmitForReview),
    takeEvery(CancelNurseDeclinedReasonReview, onCancelReview),
    takeEvery(CancelNurseDeclinedReasonReviewAndDiscard, onCancelReviewAndDiscard),
  ]);
}

export default NurseDeclinedReasonListSagas;
